import React from "react";
import { ChangeEvent } from "react";
import { Prompt, RouteComponentProps } from "react-router-dom";

import ViewToolbar from "../ViewToolbar";
import OverflowMenuItem from "../OverflowMenuItem";
import ToolbarMenuItem from "../ToolbarMenuItem";
import DeletionConfirmationDialog from "../DeletionConfirmationDialog";

import { V2 } from "../../Api";
import withTitle from "../withTitle";

import "../../blocks.css";
import "../../spinner.css";
import "../../user.css";
import "../viewer.css";
import Checkbox from "../Checkbox";

interface IEmployeeDetailsState {
    error?: string;
    isLoading: boolean;
    appointmentType?: any;

    name: string;
    description: string;
    isWellKnown: boolean;
    isPhysical: boolean;

    hasChanged: boolean;
    isSubmitting: boolean;
    submitError?: string;

    isDeleteDialogOpen: boolean;
}


class AppointmentTypeDetailsView extends React.Component<RouteComponentProps<any>, IEmployeeDetailsState> {
    constructor(props: Readonly<RouteComponentProps<any>>) {
        super(props);

        this.state = {
            isLoading: true,

            name: "",
            description: "",
            isWellKnown: false,
            isPhysical: false,

            hasChanged: false,
            isSubmitting: false,

            isDeleteDialogOpen: false,
        };

        this.submitChanges = this.submitChanges.bind(this);
        this.cancelChanges = this.cancelChanges.bind(this);
        this.handleInputChanged = this.handleInputChanged.bind(this);
        this.confirmDelete = this.confirmDelete.bind(this);
        this.closeDeleteDialog = this.closeDeleteDialog.bind(this);
        this.deleteType = this.deleteType.bind(this);
    }

    public async componentDidMount() {
        const typeId = (this.props.match && this.props.match.params.id);

        try {
            if (typeId) {
                // Load existing
                const appointmentType = await V2.get<any>(`appointments/types/${typeId}`);

                this.setState({
                    appointmentType,
                    isLoading: false,

                    name: appointmentType.name,
                    description: appointmentType.description,
                    isWellKnown: appointmentType.isWellKnown,
                    isPhysical: appointmentType.isPhysical,
                });
            }
            else {
                this.setState({
                    isLoading: false,
                });
            }
        }
        catch (e) {
            this.setState({
                error: e.message,
                isLoading: false
            });
        }
    }

    public render() {
        if (this.state.isLoading) {
            return (
                <div className="viewer-root">
                    <div className="spinner-center lds-dual-ring-big" />
                </div>
            );
        }

        if (this.state.error) {
            return (
                <div className="viewer-root">
                    <span className="error-text">{this.state.error}</span>
                </div>
            );
        }

        return (
            <div className="viewer-root with-actionbar">
                <Prompt when={this.state.hasChanged} message="Your changes will not be saved if you continue." />
                <ViewToolbar>
                    <OverflowMenuItem>
                        <ToolbarMenuItem condition={!!this.state.appointmentType && !this.state.isWellKnown}>
                            <button onClick={this.confirmDelete}><i className="icon-trash" /><span>Delete Appointment Type</span></button>
                        </ToolbarMenuItem>
                    </OverflowMenuItem>
                </ViewToolbar>
                <DeletionConfirmationDialog isOpen={this.state.isDeleteDialogOpen} onConfirm={this.deleteType} onRequestClose={this.closeDeleteDialog} onSuccess={this.cancelChanges} description={null}>
                    <span>
                        Are you sure you want to delete <b>{this.state.name}</b>?
                    </span>
                </DeletionConfirmationDialog>
                <div className="column-root input-form last-row">
                    <section className="info-block">
                        <header>
                            <h3>Appointment Type Details</h3>
                        </header>

                        <section>
                            <div className="input-form-group">
                                <label htmlFor="frm-name">Name</label>
                                <input type="text" className="input-light" id="frm-name" name="name" value={this.state.name} onChange={this.handleInputChanged} disabled={this.state.isWellKnown} autoFocus={!this.state.appointmentType} />
                            </div>
                        </section>
                        <section>
                            <div className="input-form-group">
                                <label htmlFor="frm-description">Description</label>
                                <input type="text" className="input-light" id="frm-description" name="description" value={this.state.description} onChange={this.handleInputChanged} disabled={this.state.isWellKnown} />
                            </div>
                        </section>
                        <section>
                            <div className="input-form-group">
                                <div className="input-form-group-check-inline">
                                    <Checkbox fancy={true} id="frm-is-physical" name="isPhysical" checked={this.state.isPhysical} onChange={this.handleInputChanged} autoComplete="off_please_chrome">Is Physical</Checkbox>
                                </div>
                            </div>
                        </section>
                        {this.state.isWellKnown
                            ? <section>
                                <div className="input-form-group">
                                    <small className="text-light">This appointment type is a built-in well-known appointment type.  It is used by the system internally and cannot be edited.</small>
                                </div>
                            </section>
                            : null}
                    </section>
                    <section className="block-spacer" />
                </div>
                &nbsp;
                {this.state.isSubmitting
                    ? <div className="spinner lds-dual-ring" />
                    : this.state.submitError
                        ? <span className="error-text">{this.state.submitError}</span>
                        : null}
                {this.state.isWellKnown
                    ? <div className="action-panel">
                        <button className="button blue push-right" onClick={this.cancelChanges}>Ok</button>
                    </div>
                    : <div className="action-panel">
                        <button className="button blue btn-confirm push-right" disabled={this.state.isSubmitting} onClick={this.submitChanges}>Save</button>
                        <button className="button blue btn-cancel" disabled={this.state.isSubmitting} onClick={this.cancelChanges}>Cancel</button>
                    </div>
                }
            </div>
        );
    }

    private async submitChanges() {
        try {
            if (this.state.appointmentType) {
                // Update existing appointment type
                this.setState({
                    isSubmitting: true,
                    submitError: undefined
                });

                const update = {
                    name: this.state.name,
                    description: this.state.description,
                    isPhysical: this.state.isPhysical,
                };

                await V2.patch(`appointments/types/${this.state.appointmentType.id}`, update);

                this.setState({
                    hasChanged: false,
                    isSubmitting: false
                });
            }
            else {
                // Create new appointment type
                const creationInfo = {
                    name: this.state.name,
                    description: this.state.description,
                    isPhysical: this.state.isPhysical,
                };

                this.setState({
                    isSubmitting: true,
                    submitError: undefined
                });

                await V2.post("appointments/types", creationInfo);

                this.setState({
                    hasChanged: false,
                    isSubmitting: false
                });
            }
            this.props.history.goBack();
        }
        catch (e) {
            this.setState({
                isSubmitting: false,
                submitError: e.message || "Could not save appointment type"
            });
        }
    }

    private cancelChanges() {
        this.props.history.goBack();
    }

    private handleInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) {
        const value = e.currentTarget.type === "checkbox" ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            hasChanged: true,
            [name]: value
        } as any)
    }

    private confirmDelete() {
        this.setState({
            isDeleteDialogOpen: true
        });
    }

    private async deleteType() {
        this.setState({
            hasChanged: false,
        });

        await V2.delete(`appointments/types/${this.state.appointmentType.id}`);
    }

    private closeDeleteDialog() {
        this.setState({
            isDeleteDialogOpen: false
        })
    }
}

export default withTitle("Appointment Type Details", AppointmentTypeDetailsView);
